import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { Answer } from '@/contexts/MasterclassContext';
import { supabase } from '@/integrations/supabase/client';

export const ANSWERS_QUERY_KEYS = {
  byUserId: (userId: string) => ['answers', 'user', userId] as const,
};

export const useAnswers = (userId: string | null) => {
  return useQuery({
    queryKey: ANSWERS_QUERY_KEYS.byUserId(userId || ''),
    queryFn: async () => {
      if (!userId) throw new Error('User ID is required');

      const { data, error } = await supabase.from('answers').select('*').eq('user_id', userId);

      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });
};

export const useInsertAnswer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: {
      caseProgressId: string;
      slideId: string;
      userId: string;
      answerId: string;
      caseId: string;
    }) => {
      const { data, error } = await supabase
        .from('answers')
        .insert({
          case_progress_id: variables.caseProgressId,
          slide_id: variables.slideId,
          user_id: variables.userId,
          answer_id: variables.answerId,
          case_id: variables.caseId,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    onMutate: async (variables) => {
      const tempId = `temp-${Date.now()}`;

      await queryClient.cancelQueries({
        queryKey: ANSWERS_QUERY_KEYS.byUserId(variables.userId),
      });

      const previousUserAnswers = queryClient.getQueryData(
        ANSWERS_QUERY_KEYS.byUserId(variables.userId)
      );

      const optimisticAnswer: Answer = {
        id: tempId,
        case_progress_id: variables.caseProgressId,
        slide_id: variables.slideId,
        user_id: variables.userId,
        answer_id: variables.answerId,
        case_id: variables.caseId,
        created_at: new Date().toISOString(),
      };

      console.log('DEBUG >> onMutate', optimisticAnswer, previousUserAnswers);
      // Update both queries
      queryClient.setQueryData(
        ANSWERS_QUERY_KEYS.byUserId(variables.userId),
        (old: Answer[] = []) => [...old, optimisticAnswer]
      );

      return {
        previousUserAnswers,
        tempId,
        userId: variables.userId,
        caseId: variables.caseId,
      };
    },

    onError: (err, variables, context) => {
      if (context?.previousUserAnswers) {
        queryClient.setQueryData(
          ANSWERS_QUERY_KEYS.byUserId(context.userId),
          context.previousUserAnswers
        );
      }
    },

    onSuccess: (data, variables, context) => {
      console.log('DEBUG >> onSuccess', data);
      // Replace temp answer with real one
      queryClient.setQueryData(
        ANSWERS_QUERY_KEYS.byUserId(variables.userId),
        (old: Answer[] = []) => old.map((a) => (a.id === context.tempId ? data : a))
      );
    },

    // Optional: invalidate only if you want background refresh
    // onSettled: (data, error, variables) => {
    //   queryClient.invalidateQueries({ queryKey: ANSWERS_QUERY_KEYS.byUserId(variables.userId) });
    // },
  });
};

export const useDeleteAnswers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, caseId }: { userId: string; caseId: string }) => {
      const { error } = await supabase
        .from('answers')
        .delete()
        .eq('user_id', userId)
        .eq('case_id', caseId);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ANSWERS_QUERY_KEYS.byUserId(variables.userId),
      });
    },
  });
};
